/**
 * Returns a truncated string in the middle. Eg: truncate('123456789', 2) = '12..89'
 * If the length of the string is lower than or equal to the resulting string, the original string is returned. Eg: truncate('123456', 3) = '123456' (instead of '12..56')
 * @param str String to truncate
 * @param charsOnEnds amount of chars to keep on start and same amount to keep of the end
 * @returns string
 */
export const truncate = (str: string, charsOnEnds = 4) => {
  const middleText = '..'
  const resultLength = charsOnEnds * 2 + middleText.length
  if (str.length <= resultLength) return str

  return `${str.slice(0, charsOnEnds)}..${str.slice(-charsOnEnds)}`
}

export const firstLetterToUpper = (str: string) => {
  const firstLetter = str[0]
  const rest = str.slice(1)
  return firstLetter.toLocaleUpperCase() + rest.toLocaleLowerCase()
}

export const wordsFirstLetterToUpper = (str: string) => {
  const words = str.split(' ')
  const uppercaseWords = words.map(firstLetterToUpper)
  return uppercaseWords.join(' ')
}

function isEmoji(char: string) {
  // Adjusted regex to handle a wider range of emoji characters
  const emojiRegex =
    /[\p{Emoji_Modifier_Base}\p{Emoji_Presentation}\u231A-\u231B\u23E9-\u23F3\u25AA\u25AB\u25FE\u2600-\u2604\u260E\u2611\u2614\u2615\u261D\u263A\u2648-\u2653\u2660\u2663\u2665\u2666\u2668\u267B\u267F\u2692-\u2697\u2699\u269B\u269C\u26A0-\u26A1\u26AA\u26AB\u26B0\u26B1\u26BD\u26BE\u26C4-\u26C5\u26C8\u26CE\u26CF\u26D1\u26D3-\u26D4\u26E9-\u26EA\u26F0-\u26F5\u26F7-\u26FA\u26FD\u2702\u2705\u2708-\u270D\u270F\u2712\u2714\u2716\u271D\u2721\u2733-\u2734\u2744\u2747\u274C\u274E\u2753-\u2755\u2757\u2763-\u2764\u2795-\u2797\u27A1\u27B0\u27BF\u2B05-\u2B07\u2B1B-\u2B1C\u2B50\u2B55\u3030\u303D\u3297\u3299\uD83C\uD000-\uD83D\uDFFF]/u
  return emojiRegex.test(char)
}

const defaultOptions = { withEmoji: true }
export const getInitials = (name: string, opts = defaultOptions) => {
  const words = name.split(/\s+/)
  let initials = ''

  for (let i = 0; i < Math.min(2, words.length); i++) {
    const word = words[i]
    // Use spread to get the true first Unicode character — handles emoji surrogate pairs correctly
    const firstChar = [...word][0] ?? ''

    if (isEmoji(firstChar) && opts.withEmoji) {
      const matches = word.match(/\p{Emoji}/gu) ?? []
      initials += matches.join('')
    } else if (!isEmoji(firstChar)) {
      initials += firstChar.toUpperCase()
    }
    // isEmoji && !withEmoji → skip emoji, add nothing for this word
  }

  return initials
}

export const splitByCapitalLetter = (str: string) => str.split(/(?=[A-Z])/).join(' ')
